import React, { useState, useEffect } from 'react';
import { GiStethoscope } from 'react-icons/gi';
import {
  LuMapPin,
  LuPhone,
  LuMail,
  LuGlobe,
  LuHeartPulse,
  LuUsers,
  LuLoader,
  LuExternalLink
} from 'react-icons/lu';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../shared/firebase';
import hospitalImg from '../../assets/mission_hospital.png';

const defaultHospitals = [
  {
    id: 'default-csi-mission-hospital',
    name: 'CSI Mission Hospital',
    subtitle: 'Tirunnavaya, Malappuram District',
    address: 'CSI Mission Hospital, Tirunnavaya Panchayat, Malappuram District, Kerala',
    phone: '+91 494 2600200 / +91 94473 50200',
    email: 'csimissionhospital.trny@gmail.com',
    website: 'csimissionhospital.org',
    adminOfficer: 'Mr. Dhivin Paul Puthukkattil',
    medicalSuperintendent: 'Dr. Jairaj Periyan',
    doctorsCount: '16 Doctors (4 service obligation)',
    staffCount: '55 Staff (5 service obligation)',
    established: '1970',
    image: hospitalImg,
    description: [
      "CSI Mission Hospital was established in 1970 in Tirunnavaya panchayat, a rural area in Malappuram District. This area was selected at that time by the church because health care facilities were not accessible to the majority of the population here. They had to travel to Calicut or Thrissur or Palakkad when they needed secondary care.",
      "Even now, this is the only hospital under the Malabar Diocese (and formerly under the North Kerala Diocese). The North Kerala Diocese is one of the network churches of the Christian Medical College, Vellore association, by virtue of which our students can get admission under the minority network category for the various courses at CMC. Such students have a service obligation to serve in a hospital under the respective Churches after they successfully complete their courses at CMC. So our hospital is also a boon to many such aspirants and their families.",
      "We have completed 52 years of committed service to humanity, and we are grateful to all the previous and present doctors and staff for their dedicated, sincere service in the hospital.",
      "Our Physical Medicine & Rehabilitation department is well known to a vast population far and near, as we work in collaboration with CMC, Vellore.",
      "At present, we get the services of 16 doctors (4 service obligation), and 55 staff (5 service obligation). Mr. Dhivin Paul Puthukkattil is the present Administrative Officer, and Dr. Jairaj Periyan is the Medical Superintendent."
    ]
  }
];

export default function Hospitals({ activeSection, onSelectSection }) {
  const [hospitalsList, setHospitalsList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    async function fetchHospitals() {
      try {
        setLoading(true);
        let querySnapshot;
        try {
          querySnapshot = await getDocs(collection(db, 'hospitals'));
          if (querySnapshot.empty) {
            querySnapshot = await getDocs(collection(db, 'hospital'));
          }
        } catch (e) {
          querySnapshot = await getDocs(collection(db, 'hospital'));
        }

        const fetched = [];
        if (querySnapshot && !querySnapshot.empty) {
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            const name = data.hospital_name || data.name || data.title || "CSI Mission Hospital";
            const subtitle = data.subtitle || data.location || data.place || "";
            const address = data.address || data.location || "";
            const phone = data.phone || data.mobile || data.contact || "";
            const email = data.email || "";
            const website = data.website || "";
            const adminOfficer = data.administrative_officer || data.admin_officer || data.adminOfficer || data.administrativeofficer || "";
            const medicalSuperintendent = data.medical_superintendent || data.superintendent || data.doctor || "";
            const doctorsCount = data.doctors_count || data.doctors || "";
            const staffCount = data.staff_count || data.staff || "";
            const established = data.established || "";
            const order = typeof data.order === 'number' ? data.order : 999;
            const imgUrl = data.img_url || data.image || data.img || hospitalImg;

            let description = [];
            if (Array.isArray(data.description)) {
              description = data.description;
            } else if (typeof data.description === 'string' && data.description.trim()) {
              description = data.description.split('\n\n');
            } else if (typeof data.history === 'string' && data.history.trim()) {
              description = data.history.split('\n\n');
            } else if (typeof data.about === 'string' && data.about.trim()) {
              description = data.about.split('\n\n');
            }

            fetched.push({
              id: doc.id,
              name,
              subtitle,
              address,
              phone,
              email,
              website,
              adminOfficer,
              medicalSuperintendent,
              doctorsCount,
              staffCount,
              established,
              description,
              image: imgUrl,
              order,
              raw: data
            });
          });

          fetched.sort((a, b) => a.order - b.order);
        }

        if (isMounted) {
          if (fetched.length === 0) {
            setHospitalsList(defaultHospitals);
          } else {
            const merged = fetched.map((h) => ({
              ...h,
              adminOfficer: h.adminOfficer || defaultHospitals[0].adminOfficer,
              medicalSuperintendent: h.medicalSuperintendent || defaultHospitals[0].medicalSuperintendent,
              doctorsCount: h.doctorsCount || defaultHospitals[0].doctorsCount,
              staffCount: h.staffCount || defaultHospitals[0].staffCount,
              established: h.established || defaultHospitals[0].established,
              subtitle: h.subtitle || defaultHospitals[0].subtitle,
              address: h.address || defaultHospitals[0].address,
              description: (h.description && h.description.length > 0) ? h.description : defaultHospitals[0].description
            }));
            setHospitalsList(merged);
          }
        }
      } catch (err) {
        console.error("Error fetching hospitals from Firebase:", err);
        if (isMounted) {
          setHospitalsList(defaultHospitals);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchHospitals();
    return () => { isMounted = false; };
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#5F5A58] font-sans flex flex-col pb-16">
      
      {/* Page Header */}
      <section className="relative bg-gradient-to-b from-[#FAF6F2] via-[#F5EFEE] to-[#E8DDD7] pt-24 sm:pt-32 pb-10 sm:pb-16 overflow-hidden border-b border-[#E8DDD7]">
        <div className="absolute right-[5%] top-1/2 -translate-y-1/2 pointer-events-none opacity-[0.03] select-none">
          <GiStethoscope className="w-64 h-64 sm:w-96 sm:h-96 text-[#7B1E3A]" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12 flex flex-col items-center md:items-start text-center md:text-left">
          <div className="w-16 h-1 bg-[#D4A64A] mb-4 sm:mb-6 rounded-full" />
          <h1 className="font-serif font-bold text-[#7B1E3A] text-3xl sm:text-[50px] lg:text-[56px] uppercase tracking-[0.03em] leading-[1.05]">
            Mission Hospitals
          </h1>
          <p className="mt-3 sm:mt-4 max-w-xl text-[#7B6B66] text-sm sm:text-[17px] leading-[1.6]">
            Dedicated to healing and compassionate medical care, serving rural and marginalized communities in the Malabar Diocese for over five decades.
          </p>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="max-w-[1440px] mx-auto px-4 sm:px-8 mt-6 sm:mt-12 w-full space-y-8 sm:space-y-10">
        
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[24px] border border-gray-100 shadow-sm">
            <LuLoader className="w-10 h-10 text-[#842343] animate-spin mb-3" />
            <p className="text-sm font-semibold text-[#5A4F4A]">Connecting to Firebase & loading hospitals...</p>
          </div>
        ) : (
          hospitalsList.map((hospital, idx) => {
            const cleanWebsite = hospital.website ? hospital.website.replace(/\s+/g, '') : '';
            const formattedWebsite = cleanWebsite
              ? (cleanWebsite.startsWith('http') ? cleanWebsite : `https://${cleanWebsite}`)
              : null;

            return (
              <div
                key={hospital.id || idx}
                className="bg-white rounded-[24px] sm:rounded-[32px] overflow-hidden border border-[#E8DDD7] shadow-[0_15px_40px_rgba(123,30,58,0.08)] flex flex-col lg:flex-row"
              >
                {/* Left Column - Image & Quick Info */}
                <div className="w-full lg:w-[45%] flex flex-col border-b lg:border-b-0 lg:border-r border-[#E8DDD7]">
                  <div className="relative h-60 sm:h-80 lg:h-[400px] overflow-hidden bg-[#FAF6F2] shrink-0">
                    <img 
                      src={hospital.image} 
                      alt={hospital.name} 
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = hospitalImg;
                      }}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#7B1E3A]/90 via-[#7B1E3A]/30 to-transparent"></div>
                    
                    <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6">
                      <div className="flex items-center gap-3 mb-1 sm:mb-2">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30 shrink-0">
                          <LuHeartPulse className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                        </div>
                        <div>
                          <h2 className="font-serif font-bold text-white text-xl sm:text-3xl tracking-wide leading-tight">
                            {hospital.name}
                          </h2>
                          <p className="text-white/80 font-medium text-xs sm:text-sm mt-0.5">{hospital.subtitle}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-5 sm:p-8 lg:p-10 bg-[#FAF9F7] flex-grow flex flex-col justify-center space-y-5 sm:space-y-6">
                    
                    {/* Address */}
                    {hospital.address && (
                      <div className="flex items-start gap-3.5 sm:gap-4">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white shadow-sm flex items-center justify-center shrink-0 border border-[#E8DDD7]">
                          <LuMapPin className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4A64A]" />
                        </div>
                        <div>
                          <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-[#A0AAB9] mb-0.5 sm:mb-1">Address</p>
                          <p className="text-[#333A46] font-medium text-sm sm:text-[15px] leading-relaxed">
                            {hospital.address}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Phone */}
                    {hospital.phone && (
                      <div className="flex items-start gap-3.5 sm:gap-4">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white shadow-sm flex items-center justify-center shrink-0 border border-[#E8DDD7]">
                          <LuPhone className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4A64A]" />
                        </div>
                        <div>
                          <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-[#A0AAB9] mb-0.5 sm:mb-1">Helpline &amp; Appointments</p>
                          <a href={`tel:${hospital.phone.split('/')[0].trim()}`} className="text-[#842343] font-bold text-sm sm:text-[15px] hover:underline">
                            {hospital.phone}
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Email */}
                    {hospital.email && (
                      <div className="flex items-start gap-3.5 sm:gap-4">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white shadow-sm flex items-center justify-center shrink-0 border border-[#E8DDD7]">
                          <LuMail className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4A64A]" />
                        </div>
                        <div>
                          <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-[#A0AAB9] mb-0.5 sm:mb-1">Email</p>
                          <a href={`mailto:${hospital.email}`} className="text-[#366396] hover:text-[#D4A64A] font-medium text-sm sm:text-[15px] transition-colors break-all">
                            {hospital.email}
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Website */}
                    {formattedWebsite && (
                      <div className="flex items-start gap-3.5 sm:gap-4">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white shadow-sm flex items-center justify-center shrink-0 border border-[#E8DDD7]">
                          <LuGlobe className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4A64A]" />
                        </div>
                        <div>
                          <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-[#A0AAB9] mb-0.5 sm:mb-1">Website</p>
                          <a href={formattedWebsite} target="_blank" rel="noopener noreferrer" className="text-[#366396] hover:text-[#D4A64A] font-semibold text-sm sm:text-[15px] transition-colors flex items-center gap-1 break-all">
                            <span>{cleanWebsite.replace(/^https?:\/\//, '')}</span>
                            <LuExternalLink className="w-3.5 h-3.5 shrink-0" />
                          </a>
                        </div>
                      </div>
                    )}

                  </div>
                </div>

                {/* Right Column - History & Details */}
                <div className="w-full lg:w-[55%] p-5 sm:p-8 lg:p-12 xl:p-16 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-4 sm:mb-6">
                      <h3 className="font-serif font-bold text-[#7B1E3A] text-xl sm:text-2xl">
                        About {hospital.name}
                      </h3>
                      {hospital.established && (
                        <span className="text-xs font-bold px-3 py-1 bg-[#842343]/10 text-[#842343] rounded-full shrink-0">
                          Est. {hospital.established}
                        </span>
                      )}
                    </div>

                    <div className="space-y-3.5 sm:space-y-4 text-[#5F5A58] text-sm sm:text-[15px] leading-[1.75]">
                      {hospital.description.map((paragraph, pIdx) => (
                        <p key={pIdx}>{paragraph}</p>
                      ))}
                    </div>
                  </div>

                  {/* Administration & Staffing Box */}
                  <div className="mt-6 sm:mt-8 bg-[#FAF6F2] rounded-2xl p-4 sm:p-6 border border-[#E8DDD7]">
                    <h4 className="font-sans font-bold text-[#7B1E3A] text-xs uppercase tracking-wider mb-3 sm:mb-4 flex items-center gap-2">
                      <LuUsers className="w-4 h-4 text-[#D4A64A]" />
                      Current Administration &amp; Staffing
                    </h4>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {hospital.adminOfficer && (
                        <div>
                          <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-[#8B8683] mb-0.5">Administrative Officer</p>
                          <p className="text-[#4B2233] font-bold text-sm">{hospital.adminOfficer}</p>
                        </div>
                      )}
                      {hospital.medicalSuperintendent && (
                        <div>
                          <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-[#8B8683] mb-0.5">Medical Superintendent</p>
                          <p className="text-[#4B2233] font-bold text-sm">{hospital.medicalSuperintendent}</p>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 pt-4 border-t border-[#E8DDD7]/60 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div className="bg-white rounded-xl p-3.5 text-center border border-[#E8DDD7] flex flex-col justify-center">
                        <p className="text-sm sm:text-base font-bold text-[#7B1E3A] leading-snug mb-1">{hospital.doctorsCount}</p>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#8B8683]">Doctors &amp; Specialists</p>
                      </div>
                      <div className="bg-white rounded-xl p-3.5 text-center border border-[#E8DDD7] flex flex-col justify-center">
                        <p className="text-sm sm:text-base font-bold text-[#7B1E3A] leading-snug mb-1">{hospital.staffCount}</p>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-[#8B8683]">Healthcare Staff</p>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            );
          })
        )}

      </section>
    </div>
  );
}
